import { useEffect } from "react";
import type { Section } from "../../types/api";

interface Props {
  sections: Section[];
  collapsed: Record<string, boolean>;
  onAction: (kind: HeadingActionKind, sectionId: string) => void;
  onToggleCollapse: (sectionId: string) => void;
}

export type HeadingActionKind =
  | "summarize-sec"
  | "translate-sec"
  | "copy-link"
  | "comments"
  | "tags"
  | "more";

/**
 * B3.1 — Heading affordances.
 *
 * Inject a button group at the end of each H2/H3 inside [data-prose-root].
 * Buttons: plegar/desplegar · resumir-sec · traducir-sec · copy-link · more.
 * Click delegation: the buttons set `data-h-aff-action` + `data-section-id`;
 * a single document-level listener fans actions out via `onAction` / toggle.
 */
export function HeadingAffordances({ sections, collapsed, onAction, onToggleCollapse }: Props) {
  // 1) Inject the affordance bar next to each H2/H3 with a matching data-section-id.
  useEffect(() => {
    const root = document.querySelector<HTMLElement>("[data-prose-root]");
    if (!root) return;

    // Clean any previous injections (idempotent)
    for (const old of root.querySelectorAll<HTMLElement>(".h-affordances")) old.remove();

    const headings = root.querySelectorAll<HTMLElement>("h2[data-section-id], h3[data-section-id]");
    for (const h of headings) {
      const id = h.dataset.sectionId;
      if (!id) continue;
      const sec = sections.find((s) => s.id === id);
      if (!sec) continue;
      const wrap = document.createElement("span");
      wrap.className = "h-affordances";
      wrap.dataset.section = id;
      wrap.innerHTML = renderButtons(id, sec.heading_level, collapsed[id] ?? false);
      h.appendChild(wrap);
    }

    return () => {
      for (const node of document.querySelectorAll<HTMLElement>(".h-affordances")) node.remove();
    };
  }, [sections, collapsed]);

  // 2) Click delegation.
  useEffect(() => {
    function handler(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      const btn = target?.closest<HTMLElement>("[data-h-aff-action]");
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const kind = btn.dataset.hAffAction as HeadingActionKind | "toggle";
      const sectionId = btn.dataset.sectionId;
      if (!sectionId) return;
      if (kind === "toggle") {
        onToggleCollapse(sectionId);
        return;
      }
      onAction(kind as HeadingActionKind, sectionId);
    }
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [onAction, onToggleCollapse]);

  return null;
}

function renderButtons(id: string, level: number, isCollapsed: boolean): string {
  const collapsableLevel = level === 2;
  const sep = '<span class="h-aff-sep"></span>';
  const toggle = collapsableLevel
    ? `<button type="button" class="h-aff-btn" data-h-aff-action="toggle" data-section-id="${escapeAttr(id)}" title="${isCollapsed ? "Desplegar" : "Plegar"}">${isCollapsed ? "▶" : "▼"}</button>`
    : "";
  const summarize = `<button type="button" class="h-aff-btn" data-h-aff-action="summarize-sec" data-section-id="${escapeAttr(id)}" title="Resumir esta sección">✨ Resumir</button>`;
  const translate = `<button type="button" class="h-aff-btn" data-h-aff-action="translate-sec" data-section-id="${escapeAttr(id)}" title="Traducir sección">🌐 Traducir</button>`;
  const copyLink = `<button type="button" class="h-aff-btn" data-h-aff-action="copy-link" data-section-id="${escapeAttr(id)}" title="Copiar enlace">🔗</button>`;
  const more = `<button type="button" class="h-aff-btn" data-h-aff-action="more" data-section-id="${escapeAttr(id)}" title="Más">⋯</button>`;
  return [toggle, sep, summarize, translate, copyLink, more].filter(Boolean).join("");
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
}
